<div class="settings-page locations-page">
  <div class="settings-topbar">
    <div class="topbar-left">
      <h1>
        Locations
        <small>Warehouses, stores, and garages</small>
      </h1>
      <div class="breadcrumb">
        <i class="bi bi-house-door"></i>
        Settings / <span class="current">Locations</span>
      </div>
    </div>
    <div class="topbar-right">
      <button type="button" class="btn btn-primary" (click)="openModal()">
        <i class="bi bi-plus-circle"></i> Add Location
      </button>
    </div>
  </div>

  <div class="settings-table-wrap">
    <div class="table-scroll">
      <table class="report-data-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Name</th>
            <th>Branch</th>
            <th>Type</th>
            <th>Status</th>
            <th class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (row of locations(); track row.id) {
            <tr>
              <td><span class="fw-semibold">{{ row.code }}</span></td>
              <td>{{ row.name }}</td>
              <td>{{ getBranchName(row.branchId) }}</td>
              <td>
                <span class="meta-badge location-type">
                  <i class="bi" [class]="getTypeIcon(row.type)"></i>
                  {{ row.type }}
                </span>
              </td>
              <td>
                <span class="status-pill" [class.active]="row.status === 'active'">
                  {{ row.status }}
                </span>
              </td>
              <td class="actions-cell">
                <div class="row-actions">
                  <button type="button" class="btn-action edit" title="Edit location" (click)="openEdit(row)">
                    <i class="bi bi-pencil"></i>
                  </button>
                  @if (auth.hasPermission('edit_settings')) {
                  <button type="button" class="btn-action delete" title="Delete location" (click)="deleteRow(row)">
                    <i class="bi bi-trash"></i>
                  </button>
                  }
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="6" class="text-center py-4 text-muted">
                <i class="bi bi-inbox empty-state-icon"></i>
                No locations found. Click "Add Location" to create one.
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>
</div>

@if (showModal()) {
  <div class="modal-overlay open" (click)="closeModal()">
    <div class="settings-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2 class="modal-title">
          <i class="bi" [class]="isEditing() ? 'bi-pencil' : 'bi-plus-circle'"></i>
          {{ isEditing() ? 'Edit Location' : 'Add Location' }}
        </h2>
        <button type="button" class="close-btn" (click)="closeModal()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label class="form-label">Code</label>
          @if (isEditing() && locationForm.code) {
            <input type="text" class="form-control" [value]="locationForm.code" disabled />
            <small class="text-muted">Generated automatically and cannot be changed.</small>
          } @else {
            <input type="text" class="form-control" value="Auto-generated on save" disabled />
            <small class="text-muted">A unique code is created when you save.</small>
          }
        </div>
        <div class="form-group">
          <label class="form-label">Name <span class="text-danger">*</span></label>
          <input type="text" class="form-control" [(ngModel)]="locationForm.name" placeholder="Enter location name" />
        </div>
        <div class="form-group">
          <label class="form-label">Branch <span class="text-danger">*</span></label>
          <select class="form-select" [(ngModel)]="locationForm.branchId">
            <option value="">Select branch</option>
            @for (branch of branches(); track branch.id) {
              <option [value]="branch.id">{{ branch.name }}</option>
            }
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Type</label>
          <select class="form-select" [(ngModel)]="locationForm.type">
            <option value="warehouse">Warehouse</option>
            <option value="store">Store</option>
            <option value="garage">Garage</option>
            <option value="office">Office</option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Status</label>
          <select class="form-select" [(ngModel)]="locationForm.status">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeModal()">
          <i class="bi bi-x-lg"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" (click)="save()">
          <i class="bi bi-check-lg"></i> {{ isEditing() ? 'Save Changes' : 'Save' }}
        </button>
      </div>
    </div>
  </div>
}

@if (showDeleteModal()) {
  <div class="modal-overlay open" (click)="closeDeleteModal()">
    <div class="settings-modal delete-impact-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2 class="modal-title">
          <i class="bi bi-trash"></i>
          Delete Location
        </h2>
        <button type="button" class="close-btn" (click)="closeDeleteModal()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="modal-body">
        @if (deleteLoading()) {
          <p class="text-muted mb-0">Checking for stock and items…</p>
        } @else {
          @if (deleteImpact(); as impact) {
            <p class="delete-lead">
              Delete <strong>{{ deleteTarget()?.name }}</strong>
              @if (deleteTarget()?.branchId) {
                <span class="delete-branch">
                  at {{ getBranchName(deleteTarget()!.branchId) }}
                </span>
              }?
            </p>

            @if (impact.requiresReassignment) {
              <div class="impact-banner">
                <i class="bi bi-exclamation-triangle-fill"></i>
                <div>
                  <strong>This location still has items.</strong>
                  <span>
                    {{ impact.distinctItemCount }} stock item(s)
                    ({{ impact.totalOnHand }} on hand)
                    @if (impact.assetCount > 0) {
                      · {{ impact.assetCount }} item(s)
                    }
                    must be moved before delete.
                  </span>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">
                  Move items to location <span class="text-danger">*</span>
                </label>
                <select class="form-select" [(ngModel)]="targetStoreLocationId">
                  <option value="">Select destination location</option>
                  @for (loc of destinationLocations(); track loc.id) {
                    <option [value]="loc.id">{{ loc.name }}</option>
                  }
                </select>
                @if (!destinationLocations().length) {
                  <small class="text-danger">
                    Add another active location in this branch first.
                  </small>
                }
              </div>

              @if (impact.items.length) {
                <div class="impact-items">
                  <div class="impact-items-title">Items to move</div>
                  <ul>
                    @for (item of impact.items.slice(0, 8); track item.inventoryItemId) {
                      <li>
                        <span>{{ item.itemName }}</span>
                        <span class="qty">{{ item.quantityOnHand }}</span>
                      </li>
                    }
                    @if (impact.items.length > 8) {
                      <li class="more">+{{ impact.items.length - 8 }} more…</li>
                    }
                  </ul>
                </div>
              }
            } @else {
              <p class="text-muted mb-0">
                No stock or items are linked to
                <strong>{{ deleteTarget()?.name }}</strong>
                @if (deleteTarget()?.branchId) {
                  at {{ getBranchName(deleteTarget()!.branchId) }}
                }.
                It can be deleted safely.
              </p>
              <p class="text-muted delete-hint">
                Stock shown under a similarly named location on another branch is not moved by this delete.
              </p>
            }
          }
        }

        @if (deleteError()) {
          <div class="delete-error">{{ deleteError() }}</div>
        }
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeDeleteModal()" [disabled]="deleteSubmitting()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-danger"
          (click)="confirmDelete()"
          [disabled]="deleteLoading() || deleteSubmitting() || !deleteImpact() || (deleteImpact()!.requiresReassignment && !destinationLocations().length)"
        >
          <i class="bi bi-trash"></i>
          {{ deleteSubmitting() ? 'Deleting…' : 'Delete Location' }}
        </button>
      </div>
    </div>
  </div>
}

@if (showToast()) {
  <div class="toast-container show">
    <div class="toast" [class.success]="!toastIsError()" [class.error]="toastIsError()">
      <div class="toast-icon">
        <i class="bi" [class]="toastIsError() ? 'bi-exclamation-circle-fill' : 'bi-check-circle-fill'"></i>
      </div>
      <div class="toast-content">
        <div class="toast-title">{{ toastIsError() ? 'Unable to delete' : 'Success!' }}</div>
        <div class="toast-message">{{ toastMessage() }}</div>
      </div>
    </div>
  </div>
}
